import React, { useState } from "react";
import ApiName from "../../../ApiName";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";

// Progressive Loading Image Component
const FadeInImage = ({ src, alt, className }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <>
      {!isLoaded && (
        <div className={`absolute inset-0 bg-gray-200 animate-pulse ${className}`}></div>
      )}
      <motion.img
        initial={{ opacity: 0 }}
        animate={{ opacity: isLoaded ? 1 : 0 }}
        transition={{ duration: 0.8 }}
        src={src}
        alt={alt}
        className={className}
        loading="lazy"
        onLoad={() => setIsLoaded(true)}
      />
    </>
  );
};

const ShopPage = () => {
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get("category");
  const subCategoryId = searchParams.get("subCategory");

  const { data: paints = [], isLoading } = useQuery({
    queryKey: ['paints'],
    queryFn: async () => {
      const response = await axios.get(`${ApiName}/Paint`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Filter products
  let products = [];
  let categoryNameDisplay = "All Artworks";
  
  if (!isLoading) {
    let foundCategoryName = null;
    let foundSubCategoryName = null;

    for (let i = 0; i < paints.length; i++) {
      if (paints[i].images.length !== 0) {
        let p = paints[i].paint;

        if (categoryId && p.categoryId?.toString() !== categoryId) continue;
        if (subCategoryId && p.subCategoryId?.toString() !== subCategoryId) continue;

        // Capture names for the header
        if (!foundCategoryName && p.category) foundCategoryName = p.category.name;
        if (!foundSubCategoryName && p.subCategory) foundSubCategoryName = p.subCategory.name;

        products.push({
          id: p.id,
          image: paints[i].images[0],
          name: p.paintName,
          categoryName: p.category ? p.category.name : null,
          subCategoryName: p.subCategory ? p.subCategory.name : null,
        });
      }
    }

    if (foundCategoryName) {
      categoryNameDisplay = foundSubCategoryName 
        ? `${foundCategoryName} / ${foundSubCategoryName}`
        : foundCategoryName;
    } else if (categoryId || subCategoryId) {
      categoryNameDisplay = "Filtered Artworks";
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="min-h-[85vh] pb-24"
    >
      {/* Premium Header Area */}
      <div className="w-full bg-zinc-50 border-b border-gray-200 mb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-3xl md:text-4xl sans-bold tracking-[0.2em] uppercase text-gray-900"
          >
            {categoryNameDisplay}
          </motion.h1>
          <motion.div 
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="w-16 h-[2px] bg-gray-900 mx-auto mt-6"
          ></motion.div>
        </div>
      </div>

      {/* Grid Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatePresence mode="wait">
          {isLoading ? (
            <motion.div 
              key="loading"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            >
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="w-full aspect-[4/5] bg-gray-200 animate-pulse rounded-sm shadow-sm"></div>
              ))}
            </motion.div>
          ) : products.length === 0 ? (
            <motion.div 
              key="empty"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="py-20 text-center flex flex-col items-center justify-center"
            >
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-lg text-gray-500 sans-light">No artworks found in this collection.</p>
              <Link to="/main/shop" className="mt-6 px-6 py-2 border border-gray-300 hover:border-gray-900 text-sm tracking-widest uppercase transition-colors rounded-full text-gray-600 hover:text-gray-900">
                View All Artworks
              </Link>
            </motion.div>
          ) : (
            <motion.div 
              key="grid"
              layout
              className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            >
              {products.map((x, index) => (
                <motion.div
                  layout
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.7, delay: (index % 8) * 0.1, ease: [0.16, 1, 0.3, 1] }}
                  className="relative w-full aspect-[4/5] overflow-hidden rounded-sm shadow-sm hover:shadow-xl transition-shadow duration-500 group cursor-pointer" 
                  key={x.id}
                >
                  {/* Base Image */}
                  <div className="absolute inset-0 w-full h-full z-10 overflow-hidden bg-gray-100">
                    <FadeInImage
                      src={`${ApiName}/${x.image}`}
                      alt={x.name}
                      className="object-cover w-full h-full transition-transform duration-1000 ease-out group-hover:scale-105"
                    />
                  </div>

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 z-20 flex flex-col justify-end p-6 bg-gradient-to-t from-black/90 via-black/30 to-transparent backdrop-blur-[2px] opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-500">
                    <div className="flex flex-col items-center w-full">
                      
                      {/* Name */}
                      <div className="text-xl text-center text-white sans-bold tracking-widest uppercase drop-shadow-md transform translate-y-0 md:translate-y-6 md:group-hover:translate-y-0 transition-all duration-500 ease-[0.16,1,0.3,1]">
                        {x.name}
                      </div>
                      
                      {/* Category */}
                      {(x.categoryName || x.subCategoryName) && (
                        <div className="text-[10px] text-center text-gray-300 sans-light mt-2 uppercase tracking-[0.2em] drop-shadow-sm transform translate-y-0 md:translate-y-6 md:group-hover:translate-y-0 transition-all duration-500 ease-[0.16,1,0.3,1] delay-[50ms]">
                          {x.categoryName}{x.categoryName && x.subCategoryName ? ` / ` : ""}{x.subCategoryName}
                        </div>
                      )}
                      
                      {/* Divider */}
                      <div className="w-8 border-b border-white/40 my-5 transform scale-x-100 md:scale-x-0 md:group-hover:scale-x-100 transition-transform duration-500 ease-out delay-[100ms] origin-center"></div>
                      
                      {/* Button */}
                      <div className="w-full flex justify-center transform translate-y-0 md:translate-y-6 md:group-hover:translate-y-0 transition-all duration-500 ease-[0.16,1,0.3,1] delay-[150ms]">
                        <Link to={`/main/paint/${x.id}`} className="w-full flex justify-center focus:outline-none">
                          <div className="flex items-center justify-center gap-2 px-6 py-2.5 text-xs text-white transition-all duration-300 border border-white/30 rounded-full hover:bg-white hover:text-black sans-bold uppercase tracking-[0.2em] w-3/4">
                            Details
                            <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                          </div>
                        </Link>
                      </div>

                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default ShopPage;
